import { Button, Icon, Modal, Table } from '@octano/global-ui';
import { ColumnTable } from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { requestAvailableSections } from '../../../api/requests/courseRegistration';
import { useCourseSelectionState } from '../../../hooks/useCourseSelectionState';
import { SectionType } from '../../../types/sectionType';
import { SectionsErrors } from '../../../utils/parseErrors';
import HeaderSectionList from './HeaderSectionList';
import ModalEquivalenceSelection from './ModalEquivalenceSelection';
import SectionBox from './SectionBox';
import SubheaderSectionList from './SubheaderSectionList';

/**
 * Listado de secciones de una asignatura
 */
const prefix = 'sectionList';

const SectionList = () => {
  const { t } = useTranslation();
  const {
    selectedCourse,
    setCourseState,
    selectedCampus,
    setSelectedCampus,
    selectedCareer,
    error,
    removeError,
    equivalenceOriginCourse,
    setEquivalenceOriginCourse,
  } = useCourseSelectionState();

  const [results, setResults] = useState<SectionType[]>([]);
  const [filteredSections, setFilteredSections] = useState<SectionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [errorRequirements, setErrorRequirements] = useState<
    { name: string; shortening: string }[] | null
  >(null);
  const [errorType, setErrorType] = useState<'requirements' | 'unknown' | null>(
    null,
  );

  const getAvailableSections = useCallback(async () => {
    setIsLoading(true);
    if (selectedCourse?.id && selectedCareer) {
      const { data, error } = await requestAvailableSections({
        courseId: selectedCourse.id,
        studyPlanEnrollmentId: selectedCareer.studyPlanEnrollmentId,
      });
      if (data) {
        setResults(data);
      }
      if (error) {
        // Error de pre-requisitos de asignaturas
        if (
          error?.data?.message?.[0] &&
          JSON.parse(error.data.message[0]).dependOn
        ) {
          setErrorRequirements(JSON.parse(error.data.message[0]).dependOn);
          setErrorType('requirements');
        } else {
          // Mensaje para cualquier otro error ya que no están definidos todos los tipos
          setErrorType('unknown');
        }
      }
    } else {
      setResults([]);
    }
    setIsLoading(false);
  }, [selectedCareer, selectedCourse]);

  useEffect(() => {
    if (results.length > 0 && selectedCampus) {
      setFilteredSections(
        results.filter(
          (section) => section.campus.id === selectedCampus?.value,
        ),
      );
    }
  }, [results, selectedCampus]);

  useEffect(() => {
    getAvailableSections();
  }, [getAvailableSections]);

  // HU 15417: mostrar modal de selección de equivalente cuando la asignatura
  // no tiene secciones disponibles en el período y sí tiene equivalentes
  // declaradas. Precondiciones: no estamos cargando, no hay error de
  // prerrequisitos ni tope de créditos, no hay secciones, y compatibleWith
  // no está vacío.
  const shouldShowEquivalenceModal =
    !isLoading &&
    !errorType &&
    !error?.code &&
    results.length === 0 &&
    !!selectedCourse?.compatibleWith?.length;

  const goBack = () => {
    setCourseState(null);
    setSelectedCampus(undefined);
  };

  const columns: ColumnTable<SectionType>[] = [
    {
      columnName: 'id',
      headerText: <HeaderSectionList goBackAction={goBack} />,
      cellFormat: (options) => {
        return <SectionBox options={options} />;
      },
    },
  ];

  const toggleModal = () => {
    setErrorType(null);
    setCourseState(null);
    setErrorRequirements(null);
  };

  return (
    <>
      <Table
        striped={false}
        height={700}
        subHeader={
          <SubheaderSectionList
            sections={results}
            equivalenceOrigin={equivalenceOriginCourse}
          />
        }
        columns={columns}
        data={filteredSections}
        isLoadingResults={isLoading}
        loadingView={{
          title: t(`${prefix}.loadingTitle`),
          subtitle: t(`${prefix}.loadingSubtitle`),
        }}
      />
      {/* MODAL PARA INDICAR ERRORES EN LA INSCRIPCIÓN DE ASIGNATURAS */}
      {!!errorType && (
        <Modal isOpen toggle={toggleModal}>
          <div className="text-center">
            <Row>
              <Col xs={12}>
                <Icon name="warning" size="50px" />
              </Col>
              <Col xs={12} className="pt-4">
                <span className="text-dark fs-22">
                  {t(`${prefix}.error.${errorType}.title`)}
                </span>
              </Col>
              <Col xs={12} className="pt-3 pb-4">
                <span className="fs-18 text-light fw-300">
                  {t(`${prefix}.error.${errorType}.body`)}
                </span>
              </Col>

              {errorType === 'requirements' && errorRequirements && (
                <Col xs={12} className="text-left pb-5">
                  <p className="fw-300 text-medium fs-14">
                    {t(`${prefix}.error.requirements.detailTitle`)}
                  </p>
                  <div
                    className="outlined-box-primary fs-16 text-dark px-1"
                    style={{ minHeight: '120px' }}
                  >
                    <ul>
                      {errorRequirements?.map((course, i) => {
                        return <li key={i}>{course.name}</li>;
                      })}
                    </ul>
                  </div>
                </Col>
              )}

              <Col xs={12} className="py-1 px-2">
                <Button
                  fullwidth
                  onClick={toggleModal}
                  text={t(`common.btnGotIt`)}
                />
              </Col>
            </Row>
          </div>
        </Modal>
      )}

      {/* MODAL PARA INDICAR ERRORES EN TOPE */}
      {error?.code && (
        <Modal
          isOpen
          toggle={() => {
            removeError();
          }}
        >
          <div className="text-center">
            <Row>
              <Col xs={12}>
                <Icon name="warning" size="50px" />
              </Col>
              <Col xs={12} className="pt-4">
                <span className="text-dark fs-22">
                  {t(`${prefix}.error.${error?.code}.title`)}
                </span>
              </Col>
              <Col xs={12} className="pt-3 pb-4">
                <span className="fs-18 text-light fw-300">
                  {t(`${prefix}.error.${error?.code}.body`)}
                </span>
              </Col>

              {error?.code === SectionsErrors.CONFLICT_SECTION_SCHEDULE &&
                error?.message && (
                  <Col xs={12} className="text-left pb-5">
                    <p className="fw-300 text-medium fs-14">
                      {t(`${prefix}.error.${error.code}.detailTitle`)}
                    </p>
                    <div
                      className="outlined-box-primary fs-16 text-dark px-1"
                      style={{ minHeight: '120px' }}
                    >
                      <ul>
                        {JSON.parse(error.message)?.map(
                          (sec: any, idx: number) => (
                            <li key={idx} style={{ marginBottom: '10px' }}>
                              {t(`${prefix}.sectionCode`)}: <b>{sec?.code}</b>
                              <ul>
                                <li>
                                  {t(`${prefix}.schedule`)}:{' '}
                                  <b>{sec?.horarios.join(', ')}</b>
                                </li>
                                <li>
                                  {t(`${prefix}.classroom`)}:{' '}
                                  <b>{sec?.salas.join(', ')}</b>
                                </li>
                                <li>
                                  {t(`${prefix}.teacher`)}:{' '}
                                  <b>{sec?.profesores.join(', ')}</b>
                                </li>
                              </ul>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </Col>
                )}

              <Col xs={12} className="py-1 px-2">
                <Button
                  fullwidth
                  onClick={() => {
                    removeError();
                  }}
                  text={t(`common.btnGotIt`)}
                />
              </Col>
            </Row>
          </div>
        </Modal>
      )}

      {/* MODAL HU 15417: SELECCIÓN DE ASIGNATURA EQUIVALENTE */}
      {shouldShowEquivalenceModal && (
        <ModalEquivalenceSelection
          isOpen
          options={selectedCourse?.compatibleWith ?? []}
          onCancel={() => {
            // Volver al listado de asignaturas (limpia selección y, por
            // el reducer, también limpia equivalenceOriginCourse).
            setCourseState(null);
            setSelectedCampus(undefined);
          }}
          onConfirm={(equivalent) => {
            if (!selectedCourse) {
              return;
            }
            // INVARIANTE (Task 2 JSDoc): setear origen ANTES del swap del
            // selectedCourse. El reducer preserva el origen cuando el course
            // payload no es null.
            setEquivalenceOriginCourse({
              id: selectedCourse.id,
              code: selectedCourse.code,
              name: selectedCourse.name,
            });
            setCourseState({
              id: equivalent.id,
              code: equivalent.code,
              name: equivalent.name,
              // Preservamos credits/shortening/types/semesterCourse de la
              // asignatura ORIGINAL para no romper resumen de créditos ni el
              // payload de inscripción. El BE valida vía Course.compatibleWith.
              credits: selectedCourse.credits,
              shortening: selectedCourse.shortening,
              types: selectedCourse.types,
              semesterCourse: selectedCourse.semesterCourse,
              // compatibleWith vacío en la equivalente: no queremos modal
              // recursivo si por casualidad la equivalente también tiene
              // declaradas sus propias equivalentes en otro contexto.
              compatibleWith: [],
            });
          }}
        />
      )}
    </>
  );
};

export default SectionList;
